import React from 'react';
import { NavLink, Stack, Text, Badge, Collapse, Group, rem } from '@mantine/core';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDisclosure } from '@mantine/hooks';
import {
  IconDashboard,
  IconActivity,
  IconPhoto,
  IconDownload,
  IconBell,
  IconHeart,
  IconSettings,
  IconChevronRight,
  IconCamera,
  IconChartLine,
} from '@tabler/icons-react';
import { useParentChildren } from '../../hooks/useParent';

interface NavigationItem {
  label: string;
  path: string;
  icon: React.ComponentType<{ size?: string | number; style?: React.CSSProperties }>;
  badge?: string;
  children?: NavigationItem[];
  requiredPermission?: () => boolean;
}

interface NavigationSection {
  title?: string;
  items: NavigationItem[];
}

export const ParentSidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: childrenResponse } = useParentChildren();

  // Fix data structure handling - ensure children is always an array
  const children = Array.isArray(childrenResponse)
    ? childrenResponse
    : ((childrenResponse as any)?.items || []);

  // Navigation configuration for parent portal - simplified to show only implemented routes
  const navigationSections: NavigationSection[] = [
    {
      title: 'Main',
      items: [
        {
          label: 'Dashboard',
          path: '/parent/dashboard',
          icon: IconDashboard,
        },
      ],
    },
    {
      title: 'My Children',
      items: (children || []).map((child: any) => {
        // Handle nested data structure from API
        const childData = child.data || child;
        const childAttrs = childData.attributes || childData;
        const childId = childData.id || child.id;
        const firstName = childAttrs.first_name || childAttrs.display_name?.split(' ')[0] || 'Child';
        const lastName = childAttrs.last_name || childAttrs.display_name?.split(' ').slice(1).join(' ') || '';

        return {
          label: `${firstName} ${lastName}`.trim(),
          path: `/parent/child/${childId}`,
          icon: IconHeart,
          children: [
            {
              label: 'Activities',
              path: `/parent/child/${childId}/activities`,
              icon: IconActivity,
            },
            {
              label: 'Photos',
              path: `/parent/child/${childId}/photos`,
              icon: IconPhoto,
            },
            {
              label: 'Classroom Camera',
              path: `/parent/child/${childId}/camera`,
              icon: IconCamera,
            },
            {
              label: 'AI Progress',
              path: `/parent/child/${childId}/ai-progress`,
              icon: IconChartLine,
            },
            {
              label: 'Reports',
              path: `/parent/child/${childId}/reports`,
              icon: IconDownload,
            },
          ],
        };
      }) || [],
    },
    {
      title: 'Communication',
      items: [
        {
          label: 'Notifications',
          path: '/parent/notifications',
          icon: IconBell,
          badge: '3', // Mock notification count
        },
      ],
    },
    {
      title: 'Reports',
      items: [
        {
          label: 'Daily Reports',
          path: children.length > 0
            ? `/parent/child/${children[0]?.data?.id || children[0]?.id}/reports`
            : '/parent/daily-reports',
          icon: IconDownload,
        },
      ],
    },
    {
      title: 'Settings',
      items: [
        {
          label: 'Preferences',
          path: '/parent/settings',
          icon: IconSettings,
        },
      ],
    },
  ];

  const NavigationLink: React.FC<{ item: NavigationItem; depth?: number }> = ({
    item,
    depth = 0
  }) => {
    const [opened, { toggle }] = useDisclosure(false);
    const isActive = location.pathname === item.path ||
                     (item.children?.some(child => location.pathname === child.path));
    const hasChildren = item.children && item.children.length > 0;

    const handleClick = () => {
      if (hasChildren) {
        toggle();
      } else {
        navigate(item.path);
      }
    };

    return (
      <>
        <NavLink
          key={item.path}
          label={item.label}
          leftSection={<item.icon style={{ width: rem(16), height: rem(16) }} />}
          rightSection={
            <Group gap={4}>
              {item.badge && (
                <Badge size="xs" variant="filled" color="blue">
                  {item.badge}
                </Badge>
              )}
              {hasChildren && (
                <IconChevronRight
                  style={{
                    width: rem(12),
                    height: rem(12),
                    transform: opened ? 'rotate(90deg)' : undefined,
                    transition: 'transform 0.2s ease',
                  }}
                />
              )}
            </Group>
          }
          active={isActive && !hasChildren}
          onClick={handleClick}
          style={{
            paddingLeft: rem(12 + depth * 20),
            borderRadius: rem(6),
            marginBottom: rem(2),
          }}
          classNames={{
            root: 'parent-nav-link',
          }}
        />

        {hasChildren && (
          <Collapse in={opened}>
            <Stack gap={2} ml="md">
              {item.children!.map((child) => (
                <NavigationLink key={child.path} item={child} depth={depth + 1} />
              ))}
            </Stack>
          </Collapse>
        )}
      </>
    );
  };

  return (
    <Stack gap="lg" style={{ height: '100%', overflow: 'auto' }}>
      {navigationSections.map((section, index) => (
        <div key={index}>
          {section.title && (
            <Text
              size="xs"
              fw={600}
              c="dimmed"
              tt="uppercase"
              mb="sm"
              style={{ letterSpacing: '0.05em' }}
            >
              {section.title}
            </Text>
          )}
          <Stack gap={2}>
            {section.items
              .filter((item) => !item.requiredPermission || item.requiredPermission())
              .map((item) => (
                <NavigationLink key={item.path} item={item} />
              ))}
          </Stack>
        </div>
      ))}

      {/* Bottom spacing for better visual balance */}
      <div style={{ flex: 1 }} />
    </Stack>
  );
};

export default ParentSidebar;
